"use client";

import clsx from "clsx";
import { FC } from "react";

import { cn } from "@/lib/utils";
import IconArrowRight from "../../Icons/IconArrowRight";

interface ArrowNavigationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  className?: string;
  // Accessibility props
  prevLabel?: string;
  nextLabel?: string;
  ariaCurrent?: "page" | "step" | "location" | "date" | "time" | boolean;
  ariaLabel?: string;
}

const ArrowNavigation: FC<ArrowNavigationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage = 10,
  onPageChange,
  className,
  prevLabel = "Previous page",
  nextLabel = "Next page",
  ariaCurrent = "page",
  ariaLabel: _ariaLabel = "Pagination navigation",
}) => {
  if (totalItems === 0) {
    return null;
  }

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    const visiblePages = [];

    if (totalPages <= 4) {
      return pages;
    }

    // Always show first page
    visiblePages.push(1);

    if (currentPage > 3) {
      visiblePages.push("...");
    }

    // Show current page and surrounding pages
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      visiblePages.push(i);
    }

    if (currentPage < totalPages - 2) {
      visiblePages.push("...");
    }

    // Always show last page
    visiblePages.push(totalPages);

    return visiblePages;
  };

  return (
    <div className={clsx("flex items-center gap-2", className)}>
      <div className="inter flex items-center gap-2 font-medium" role="list">
        {getVisiblePages().map((page, index) => (
          <div key={index} role="listitem">
            {typeof page === "number" ? (
              <button
                onClick={() => onPageChange(page)}
                disabled={page === currentPage}
                className={cn(
                  "inter me-2 h-8 rounded-lg px-2 text-[20px] font-semibold transition-colors hover:cursor-pointer hover:text-black",
                  page === currentPage ? "text-black hover:cursor-default" : "text-gray-400 hover:bg-gray-100",
                )}
                aria-current={page === currentPage ? ariaCurrent : undefined}
                aria-label={`Page ${page}`}
              >
                {page}
              </button>
            ) : (
              <span className="text-gray-400" aria-hidden="true">
                {page}
              </span>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={cn(
          "me-2 flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:cursor-pointer",
          currentPage === 1
            ? "bg-gray-200 text-gray-400 hover:cursor-default"
            : "bg-primary text-white hover:bg-primary/90",
        )}
        aria-label={prevLabel}
        aria-disabled={currentPage === 1}
      >
        <IconArrowRight fill={currentPage === 1 ? "#9CA3AF" : "white"} className="rotate-180" aria-hidden="true" />
      </button>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:cursor-pointer",
          currentPage === totalPages
            ? "bg-gray-200 text-gray-400 hover:cursor-default"
            : "bg-primary text-white hover:bg-primary/90",
        )}
        aria-label={nextLabel}
        aria-disabled={currentPage === totalPages}
      >
        <IconArrowRight fill={currentPage === totalPages ? "#9CA3AF" : "white"} aria-hidden="true" />
      </button>
    </div>
  );
};

export default ArrowNavigation;
